import { DEVICE_TYPES } from "app/data-studio-redesign/interfaces/data-studio.interface"

export type DataStudioParks = {
  code: string,
  controlSystem: DevicePark[]
  data30sAvailable: boolean,
  energyMeterAvailable: boolean,
  fe_name: string,
  metmasts: MetmastDevicePark[],
  name: string,
  turbines: TurbinePark[]
}

export type DevicePark = {
  _id: string,
  name: string,
  code: string,
  selected?: boolean
}

export type MetmastDevicePark = DevicePark & {
  sensorsList: {
    _id: string;
    name: string;
    code: string;
  }[]
}

export type TurbinePark = DevicePark & {
  serialNav: number,
  type: string
}

export type TurbineDataStudioLive = TurbinePark & {
  displayName: string,
  value: number,
  parkCode: string,
  name: string,
  id: string,
  parkName: string,
  code: string
}

export type QueryParams = {
  queryType: string,
  granularity: string,
  startTS: number,
  endTS: number
};

export type CSSignal = {
  displayName: string,
  dbName: string,
  deviceId: string[],
  unit: string,
  checked?: boolean
}

export type MetmastSignal = {
  displayName: string,
  metmastId?: string,
  dbName: string,
  deviceId?: string,
  unit: string,
  checked?: boolean
}

export type TurbineSignal = {
  name: string;
  displayName?: string;
  displayShortName?: string;
  signalGroupType?: TurbineSignalGroup;
  description: string;
  deviceId: string,
  unit: string;
  value?: string;
  checked?: boolean;
  turbineType: string;
}

export type SignalGroupName = {
  name: TurbineSignalGroup;
  checked: boolean;
}

export type SignalGroupElement = {
  signalGroupName: SignalGroupName;
  signals: TurbineSignal[];
}

export const TurbineSignalGroups = {
  ambient: 'ambient',
  electrical: 'electrical',
  technical: 'technical',
  temperature: 'temperature'
} as const;

export type TurbineSignalGroup = typeof TurbineSignalGroups[keyof typeof TurbineSignalGroups];

export type TurbineDataWithSignals = {
  _id: string;
  name: string;
  type: string;
  code: string;
  serialNav: number;
} & Record<TurbineSignalGroup, TurbineSignal[]>;

export type CSDataWithSignals = DevicePark & {
  signalsList: CSSignal[]
};

export type MetmastsDataWithSignals = DevicePark & {
  sensorsList: MetmastSensor[]
}

export type MetmastSensor = {
  name: string,
  _id: string,
  _id_metmast: string,
  signalsList: MetmastSignal[],
}

export type Metmast = {
  name: string,
  _id: string,
  sensorsList: MetmastSensor[],
}

export type MetmastSensorSelection = MetmastSensor & SelectionProps;
export type MetmastSelection = Omit<Metmast, 'sensorsList'> & { sensorsList: MetmastSensorSelection[] } & SelectionProps;

export type SelectionProps = {
  checked: boolean,
  total: number,
  totalSelected: number
}

export type SelectedParksTurbines = {
  parkCode: string,
  turbines: TurbinePark[]
}

export const AGG_TYPES = {
  PARK: 'park',
  TURBINE: 'turbine',
  TURBINE_TYPE: 'turbine_type'
} as const;

export const CHART_TYPES = {
  NORMAL: 'NORMAL',
  MULTIYAXIS: 'MULTIYAXIS',
  TREND: 'TREND',
  MULTITURBINE: 'MULTITURBINE'
} as const;

export type ChartType = typeof CHART_TYPES[keyof typeof CHART_TYPES];

export type DeviceType = typeof DEVICE_TYPES[keyof typeof DEVICE_TYPES];

export type AggregationType = typeof AGG_TYPES[keyof typeof AGG_TYPES];

export type BaseSignalItem = {
  "Date UTC": string; // "DD/MM/YYYY HH:MM:SS"
  "User Date": string; // "DD/MM/YYYY HH:MM:SS"
};

export type SignalMeasurement = {
  [key: `SIGNALS.${string}.DISPLAY_NAME(${string})`]: string | number;
};

export type TurbineData = BaseSignalItem & Partial<SignalMeasurement> & {
  "Turbine": string;
  "Turbine Date": string;
};

export type MetmastData = BaseSignalItem & Partial<SignalMeasurement> & {
  "Metmast Sensor": string;
  "Metmast Sensor Date": string;
};

export type ControlSystemData = BaseSignalItem & Partial<SignalMeasurement> & {
  "Control System": string;
  "Control System Date": string;
};

export type ParkData = BaseSignalItem & Partial<SignalMeasurement> & {
  "Park": string;
};

export type MinMaxSignals = {
  min: {
    Timestamp: number;
    ["Control System Date"]?: string;
    ["Turbine Date"]?: string;
    ["Metmast Sensor Date"]?: string;
    [signalKey: `SIGNALS.${string}.DISPLAY_NAME(${string})`]: number;
  };
  max: {
    Timestamp: number;
    ["Control System Date"]?: string;
    ["Turbine Date"]?: string;
    ["Metmast Sensor Date"]?: string;
    [signalKey: `SIGNALS.${string}.DISPLAY_NAME(${string})`]: number;
  };
};

export const deviceKeys = [
  'Park',
  'Turbine',
  'Metmast Sensor',
  'Control System'
] as const;

export const previewTypes = ['signal', 'device'] as const;

export type DSSignalItemData = TurbineData | MetmastData | ControlSystemData | ParkData;

export type DataSelectOptions = {
  aggregationLevels: string[];
  aggregationLevel: string;
  assetSelected: any;
  assetPark: any;
  dateFrom: number;
  dateTo: number;
  granularities: string[];
  granularity: number;
  estimatedRows: number;
  estimatedFields: number;
  estimatedFileSize: number;
  fileRequest: {
    deviceIds?: string[];
    deviceType?: DeviceType
    endTS: number;
    fileName: string;
    fileType: string;
    granularity: string;
    queryType: string;
    signals: string[];
    startTS: number;
    turbines?: string[];
    wrongDate: boolean;
  };
}

export type DevicesList = {
  id: string;
  name: string;
  selected: boolean;
  signals: SignalList[]
}

export type SignalList = {
  name: string;
  displayName: string;
  unit: string;
  selected: boolean;
}

export type SignalMultiDevicesList = {
  name: string;
  displayName: string;
  selected: boolean;
  devices: MultiDeviceList[]
}

export type MultiDeviceList = {
  id: string;
  name: string;
  selected: boolean;
}

export type PreviewSerieType = {
  type: string;
  name: string;
  unit: string;
  yAxis?: number,
  legendSymbol: string;
  data: [number, number][]
}
